import { useTranslation } from 'next-i18next'
import { useMemo } from 'react'

import type { Language, SupportedMarketKey } from '@nxt-insurance/constants'

import { resolveFlowSuccessData } from '@/app/utils/flows/flowSuccessData'
import { useFormatting } from '@/context/FormattingContext'
import type { AppliedCoupon } from '@/hooks/useCoupon'
import type {
  PermanentDiscountData,
  ReferralDiscountData,
} from '@flows-ui/services/QuoteDataContext.types'
import type { QuoteDataContextValue } from '@flows-ui/stepRenderer'
import { resolveAppliedDiscount } from '@flows-ui/utils/discounts'
import type { FlowValues } from '@flows/core/types/stepTypes'
import type { CoveragePlan } from '@flows/index'
import {
  clampToEarliestStartDate,
  getRequestedStartDate,
} from '@flows/insurance/utils/startDateUtils'

import type { FlowStepPageConfig } from './types'

export const useFlowSummary = ({
  config,
  appliedCoupon,
  flowValues,
  grossPremium,
  language,
  market,
  permanentDiscount,
  productGeneration,
  referralDiscount,
  selectedPlan,
}: {
  config: FlowStepPageConfig
  appliedCoupon: AppliedCoupon | null
  flowValues: FlowValues
  grossPremium: number
  language: Language
  market: SupportedMarketKey
  permanentDiscount: PermanentDiscountData | null
  productGeneration: QuoteDataContextValue['productGeneration']
  referralDiscount: ReferralDiscountData | null
  selectedPlan: CoveragePlan | undefined
}) => {
  const { t } = useTranslation()
  const formatter = useFormatting()

  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(`${language}-${market}`, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }),
    [language, market]
  )

  const products = (flowValues.products ?? {}) as Record<
    string,
    { configuration_parameters?: Record<string, unknown> } | undefined
  >
  const configParams = products[config.productKey]?.configuration_parameters ?? {}
  const selectedPlanTitle = selectedPlan?.title ?? ''

  const priceInterval =
    config.fixedPriceInterval ?? (flowValues.premium_interval === 'P1Y' ? 'P1Y' : 'P1M')
  const formattedPremium = formatter.formatPrice(grossPremium)

  const appliedDiscount = resolveAppliedDiscount({
    couponInfo: appliedCoupon,
    grossPremium,
    permanentDiscount,
    referralDiscount,
  })
  const formattedFinalPremium = formatter.formatPrice(appliedDiscount.finalPrice)

  const priceLabel =
    priceInterval === 'P1Y'
      ? t('sharedFlows_common:orderSummary.quote.summary.P1Y')
      : t('sharedFlows_common:orderSummary.quote.summary.P1M')

  const requestedStartDate = getRequestedStartDate(flowValues, config.productKey)
  const startDate = requestedStartDate
    ? clampToEarliestStartDate(requestedStartDate, config.productKey)
    : null
  const formattedStartDate = startDate ? dateFormatter.format(startDate) : ''
  const renewalDate = startDate
    ? dateFormatter.format(
        new Date(startDate.getFullYear() + 1, startDate.getMonth(), startDate.getDate())
      )
    : ''

  const naturalDisastersEligible = configParams?.natural_disasters_coverage_eligible !== false
  const activeExtensions =
    config.supportsExtensions === false
      ? []
      : (productGeneration?.extensions ?? []).filter(
          (extension) =>
            configParams[extension.configurationDataExtensionKey] === true &&
            (naturalDisastersEligible ||
              !extension.configurationDataExtensionKey.includes('natural_disasters'))
        )

  const successData = resolveFlowSuccessData({
    flowName: config.flowName,
    current: {
      selectedPlanTitle,
      formattedPremium:
        appliedDiscount.type === 'coupon' ? formattedPremium : formattedFinalPremium,
      priceLabel,
      formattedStartDate,
    },
    hasPrice: grossPremium > 0,
    emptyPremium: formatter.formatPrice(0),
  })

  return {
    activeExtensions,
    appliedDiscount,
    formattedFinalPremium,
    formattedPremium,
    formattedStartDate,
    grossPremium,
    priceInterval,
    priceLabel,
    renewalDate,
    selectedPlanTitle,
    successData,
  }
}
